<script setup>
import { computed, ref, watch, onMounted } from 'vue';

const mainLogoError = ref(false);

const props = defineProps({
  card1Start: { type: [Number, String], default: 0 },
  card1: { type: [Number, String], default: 363416.28 },
  card1Middle: { type: [Number, String], default: 180000 },
  card2Start: { type: [Number, String], default: 0 },
  card2: { type: [Number, String], default: 91696.62 },
  card2Middle: { type: [Number, String], default: 45000 },
  card3Start: { type: [Number, String], default: 0 },
  card3: { type: [Number, String], default: 11245.19 },
  card3Middle: { type: [Number, String], default: 5600 },
  card4Start: { type: [Number, String], default: 0 },
  card4: { type: [Number, String], default: 542.28 },
  card4Middle: { type: [Number, String], default: 270 },
  secondsPerUnit: { type: Number, default: 0.5 },
  currencyCode: { type: String, default: 'TRY' },
});

const displayValues = ref([0, 0, 0, 0]);
const pulsingCards = ref([false, false, false, false]);

const formatValue = (val) => {
  const n = Number(val);
  if (isNaN(n)) return '0.00';
  const [int, dec] = n.toFixed(2).split('.');
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + '.' + dec;
};

const linear = (t) => t;

const STORAGE_KEY_START = 'jackpotCloverAnimationStart';
const STORAGE_KEY_CONFIG = 'jackpotCloverCardConfig';

const getCardDuration = (start, end) => {
  const range = Math.abs(Number(end) - Number(start));
  const secPerUnit = props.secondsPerUnit ?? 0.5;
  return Math.max(500, (range / 0.01) * secPerUnit * 1000);
};

const cardProps = computed(() => [
  { start: props.card1Start, end: props.card1, middle: props.card1Middle },
  { start: props.card2Start, end: props.card2, middle: props.card2Middle },
  { start: props.card3Start, end: props.card3, middle: props.card3Middle },
  { start: props.card4Start, end: props.card4, middle: props.card4Middle },
]);

const saveAnimationState = () => {
  try {
    localStorage.setItem(STORAGE_KEY_START, String(Date.now()));
    localStorage.setItem(
      STORAGE_KEY_CONFIG,
      JSON.stringify({
        ends: [props.card1, props.card2, props.card3, props.card4],
        starts: [props.card1Start, props.card2Start, props.card3Start, props.card4Start],
        secondsPerUnit: props.secondsPerUnit,
      })
    );
  } catch (_) {}
};

const loadAndResume = () => {
  try {
    const startStr = localStorage.getItem(STORAGE_KEY_START);
    const configStr = localStorage.getItem(STORAGE_KEY_CONFIG);
    if (!startStr || !configStr) return false;

    const startTime = Number(startStr);
    const config = JSON.parse(configStr);
    const currentEnds = [props.card1, props.card2, props.card3, props.card4].map(Number);
    const savedEnds = (config.ends || []).map(Number);
    const sameConfig =
      config.secondsPerUnit === props.secondsPerUnit &&
      savedEnds.length === 4 &&
      currentEnds.every((v, i) => Math.abs(v - (savedEnds[i] ?? 0)) < 0.01);

    if (!sameConfig) return false;

    const elapsed = Date.now() - startTime;
    const cards = cardProps.value;

    for (let i = 0; i < 4; i++) {
      const cp = cards[i];
      const duration = getCardDuration(cp.start, cp.end);
      const cycleProgress = (elapsed % duration) / duration;
      const eased = linear(cycleProgress);
      const startNum = Number(cp.start) || 0;
      const endNum = Number(cp.end) || 0;
      const middleNum = cp.middle != null && cp.middle !== '' ? Number(cp.middle) : null;
      const current = startNum + (endNum - startNum) * eased;

      displayValues.value[i] = current;
      pulsingCards.value[i] =
        middleNum != null &&
        ((startNum <= endNum && current >= middleNum) || (startNum > endNum && current <= middleNum));

      animateValue(i, cp.start, cp.end, cp.middle, {
        startFrom: current,
        durationMs: duration * (1 - cycleProgress),
        loop: true,
      });
    }
    return true;
  } catch (_) {
    return false;
  }
};

const animateValue = (index, start, end, middle, opts = {}) => {
  const startNum = Number(start) || 0;
  const endNum = Number(end) || 0;
  const middleNum = middle != null && middle !== '' ? Number(middle) : null;
  const duration = opts.durationMs ?? getCardDuration(start, end);
  const animStart = opts.startFrom;
  const useResume = animStart != null;
  const loop = opts.loop === true;

  const startTime = performance.now();
  if (!useResume) pulsingCards.value[index] = false;

  const runCycle = (fromVal, dur) => {
    const cycleStart = performance.now();

    const step = (now) => {
      const elapsed = now - cycleStart;
      const progress = Math.min(elapsed / dur, 1);
      const eased = linear(progress);
      const current = fromVal + (endNum - fromVal) * eased;
      displayValues.value[index] = current;

      if (middleNum != null) {
        const reachedMiddle = (startNum <= endNum && current >= middleNum) || (startNum > endNum && current <= middleNum);
        if (reachedMiddle) {
          pulsingCards.value[index] = true;
        }
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        displayValues.value[index] = endNum;
        pulsingCards.value[index] = false;
        if (loop) {
          displayValues.value[index] = startNum;
          runCycle(startNum, getCardDuration(start, end));
        }
      }
    };

    requestAnimationFrame(step);
  };

  runCycle(useResume ? animStart : startNum, duration);
};

const runAnimations = () => {
  const resumed = loadAndResume();
  if (resumed) return;

  displayValues.value = [
    Number(props.card1Start) || 0,
    Number(props.card2Start) || 0,
    Number(props.card3Start) || 0,
    Number(props.card4Start) || 0,
  ];
  pulsingCards.value = [false, false, false, false];
  saveAnimationState();

  const cards = cardProps.value;
  cards.forEach((cp, i) => animateValue(i, cp.start, cp.end, cp.middle, { loop: true }));
};

onMounted(runAnimations);

watch(
  () => [props.card1, props.card2, props.card3, props.card4, props.card1Start, props.card2Start, props.card3Start, props.card4Start, props.card1Middle, props.card2Middle, props.card3Middle, props.card4Middle, props.secondsPerUnit],
  runAnimations,
  { deep: true }
);

const jackpots = computed(() =>
  [0, 1, 2, 3].map((i) => ({
    value: displayValues.value[i],
    pulsing: pulsingCards.value[i],
    bgImage: `/jackpot-cards/clover${i + 1}.png`,
  }))
);

const jackpotsGrandMajor = computed(() => jackpots.value.slice(0, 2));
const jackpotsMinorMini = computed(() => jackpots.value.slice(2, 4));
</script>

<template>
  <div class="jackpot-clover">
    <div class="jackpot-clover__logo-wrap">
      <img
        v-show="!mainLogoError"
        src="/jackpot-cards/clover-logo.png"
        alt="Clover Chance"
        class="jackpot-clover__logo"
        @error="mainLogoError = true"
      />
      <div v-show="mainLogoError" class="jackpot-clover__logo-fallback">
        <span class="jackpot-clover__title">Clover Chance</span>
      </div>
    </div>

    <div class="jackpot-clover__bars">
      <div
        v-for="(jp, i) in jackpotsGrandMajor"
        :key="'grand-' + i"
        class="jackpot-clover__bar jackpot-clover__bar--full"
        :class="[
          { 'jackpot-clover__bar--pulsing': jp.pulsing },
          i === 0 ? 'jackpot-clover__bar--grand' : 'jackpot-clover__bar--major'
        ]"
        :style="{ backgroundImage: `url(${jp.bgImage})` }"
      >
        <div class="jackpot-clover__content" :class="i === 0 ? 'jackpot-clover__content--grand' : 'jackpot-clover__content--major'">
          <div class="jackpot-clover__value-wrap">
            <span class="jackpot-clover__value">{{ formatValue(jp.value) }}</span>
          </div>
          <div class="jackpot-clover__currency-section">
            <div class="jackpot-clover__currency-divider"></div>
            <div class="jackpot-clover__currency-wrap">
              <span
                v-for="letter in (currencyCode || 'TRY').split('')"
                :key="letter"
                class="jackpot-clover__currency-letter"
              >{{ letter }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="jackpot-clover__row-minor-mini">
        <div
          v-for="(jp, i) in jackpotsMinorMini"
          :key="'minor-' + i"
          class="jackpot-clover__bar jackpot-clover__bar--half"
          :class="{ 'jackpot-clover__bar--pulsing': jp.pulsing }"
          :style="{ backgroundImage: `url(${jp.bgImage})` }"
        >
          <div class="jackpot-clover__content jackpot-clover__content--minor">
            <div class="jackpot-clover__value-wrap">
              <span class="jackpot-clover__value">{{ formatValue(jp.value) }}</span>
            </div>
            <div class="jackpot-clover__currency-section">
              <div class="jackpot-clover__currency-divider"></div>
              <div class="jackpot-clover__currency-wrap">
                <span
                  v-for="letter in (currencyCode || 'TRY').split('')"
                  :key="letter"
                  class="jackpot-clover__currency-letter"
                >{{ letter }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.jackpot-clover {
  @apply mx-2 mt-3 mb-2;
  background: transparent;
  /* background: linear-gradient(180deg, var(--color-primary1) 0%, var(--color-primary5) 100%); */
  border-radius: 10px;
  padding: 0.65rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.jackpot-clover__logo-wrap {
  @apply flex flex-col items-center mb-3;
}

.jackpot-clover__logo {
  width: 30%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.jackpot-clover__logo-fallback {
  text-align: center;
}

.jackpot-clover__title {
  font-size: 1.25rem;
  font-weight: 800;
  background: linear-gradient(135deg, #e8c547 0%, #9c27b0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.08em;
}

.jackpot-clover__bars {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  overflow: visible;
  padding: 0 0.5rem;
  max-width: 100%;
}

.jackpot-clover__bar {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
  overflow: visible;
}

.jackpot-clover__bar--full {
  height: auto;
}

.jackpot-clover__bar--grand {
  width: 300px;
  min-height: 80px;
  padding: 0 1rem 0.35rem;
}

.jackpot-clover__bar--major {
  width: 250px;
  min-height: 68px;
  padding: 0 1rem 0.3rem;
}

.jackpot-clover__bar--half {
  width: 140px;
  min-width: 140px;
  min-height: 52px;
  padding: 0 0.75rem 0.35rem;
}

.jackpot-clover__row-minor-mini {
  display: flex;
  flex-direction: row;
  gap: 18px;
}

.jackpot-clover__bar--pulsing {
  animation: jackpot-clover-pulse 0.8s ease-in-out infinite;
}

@keyframes jackpot-clover-pulse {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.15); }
}

.jackpot-clover__content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 0 0.5rem;
}

.jackpot-clover__value-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.35rem;
}

.jackpot-clover__value {
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.08em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

.jackpot-clover__content--grand .jackpot-clover__value {
  font-size: 1.65rem;
}

.jackpot-clover__content--major .jackpot-clover__value {
  font-size: 1.45rem;
}

.jackpot-clover__content--minor .jackpot-clover__value {
  font-size: 0.95rem;
}

.jackpot-clover__currency-section {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
}

.jackpot-clover__currency-divider {
  width: 1px;
  background: rgba(255, 255, 255, 0.4);
  margin: 0 0.35rem;
}

.jackpot-clover__currency-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  padding: 0.06rem 0.08rem;
}

.jackpot-clover__currency-letter {
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.1;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}

.jackpot-clover__content--grand .jackpot-clover__currency-letter {
  font-size: 0.6rem;
}

.jackpot-clover__content--major .jackpot-clover__currency-letter {
  font-size: 0.55rem;
}

.jackpot-clover__content--minor .jackpot-clover__currency-letter {
  font-size: 0.45rem;
}
</style>